import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Wrench, CalendarCheck, Workflow, ArrowRight, MapPin } from "lucide-react";
import DemoNotice from "@/components/site/DemoNotice";

const HERO_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/51ddf5f6-e80c-4259-9ef6-c5393e7b5903/images/dd575c7557e7d72528f8d94bb53752a0ac5d546bb142e6d9098a6c4d0ab73cf8.png";

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      aria-labelledby="hero-heading"
      className="relative overflow-hidden bg-navy text-white"
    >
      {/* gradient & decorative layers */}
      <div className="absolute inset-0 bg-brand-gradient" aria-hidden="true" />
      <div
        className="absolute inset-0 opacity-[0.18] mix-blend-screen"
        style={{
          backgroundImage:
            "radial-gradient(circle at 18% 30%, rgba(34,211,238,0.35) 0%, transparent 45%), radial-gradient(circle at 88% 78%, rgba(245,158,11,0.18) 0%, transparent 50%)",
        }}
        aria-hidden="true"
      />
      <div className="absolute inset-x-0 top-0 h-[1px] bg-sun-stripe" aria-hidden="true" />

      {/* soft airflow lines */}
      <svg
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 left-0 hidden h-full w-1/2 opacity-[0.18] lg:block"
        viewBox="0 0 600 800"
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id="flow" x1="0" x2="1">
            <stop offset="0" stopColor="#22D3EE" stopOpacity="0" />
            <stop offset="0.5" stopColor="#22D3EE" stopOpacity="0.9" />
            <stop offset="1" stopColor="#22D3EE" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[120, 230, 340, 460, 580, 690].map((y, i) => (
          <path
            key={y}
            d={`M -50 ${y} C 200 ${y - 40 + i * 6} 380 ${y + 40 - i * 4} 700 ${y}`}
            stroke="url(#flow)"
            strokeWidth="1.2"
            fill="none"
          />
        ))}
      </svg>

      <div className="container-page relative grid grid-cols-1 items-center gap-12 py-16 sm:py-20 lg:grid-cols-12 lg:gap-10 lg:py-28">
        {/* Copy column */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="lg:col-span-7"
        >
          <span className="pill-dark">
            <MapPin className="h-3 w-3 text-aqua" />
            Southwest Florida · Lee &amp; Collier County
          </span>
          <h1
            id="hero-heading"
            className="mt-5 font-display text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl"
          >
            AC not cooling in Southwest&nbsp;Florida?
            <span className="block text-aqua">Start a clear service request.</span>
          </h1>
          <p className="mt-5 max-w-xl text-base text-white/80 sm:text-lg">
            Tell us what&rsquo;s happening, choose your urgency, and show the
            contractor exactly what&rsquo;s going on &mdash; all before the
            callback.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
            <Link
              to="/request-service"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-sun px-6 py-3.5 text-base font-semibold text-navy-deep shadow-sunGlow transition-transform hover:scale-[1.02]"
            >
              <Wrench className="h-4 w-4" />
              Request HVAC Service
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/request-service?intent=maintenance"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white/95 px-6 py-3.5 text-base font-semibold text-navy shadow-soft transition-colors hover:bg-white"
            >
              <CalendarCheck className="h-4 w-4 text-aqua-deep" />
              Schedule Maintenance
            </Link>
            <Link
              to="/demo"
              data-testid="hero-cta-tertiary"
              className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-sun/70 px-5 py-3 text-base font-semibold text-sun transition-colors hover:bg-sun/10"
            >
              <Workflow className="h-4 w-4" />
              See How This Demo Works
            </Link>
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <DemoNotice variant="light" />
            <span className="text-xs text-white/55">
              No emergency dispatch promises · Phone number is a demo placeholder
            </span>
          </div>
        </motion.div>

        {/* Image column */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.15, ease: "easeOut" }}
          className="relative lg:col-span-5"
        >
          <div className="relative mx-auto aspect-[4/5] w-full max-w-md overflow-hidden rounded-3xl border border-white/10 shadow-lift sm:aspect-[5/6]">
            <img
              src={HERO_IMAGE}
              alt="Southwest Florida home at golden hour with an outdoor AC condenser unit beside the house."
              className="h-full w-full object-cover"
              loading="eager"
            />
            <div
              className="absolute inset-0 bg-gradient-to-tr from-navy/65 via-navy/10 to-transparent"
              aria-hidden="true"
            />
            {/* floating stat chip */}
            <div className="absolute bottom-4 left-4 right-4 rounded-2xl border border-white/15 bg-white/10 p-3 backdrop-blur-md">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-aqua/20 text-aqua">
                  <Wrench className="h-4 w-4" />
                </span>
                <div className="leading-tight">
                  <p className="text-[11px] uppercase tracking-[0.16em] text-white/65">
                    Smart intake demo
                  </p>
                  <p className="text-sm font-semibold text-white">
                    Issue &middot; Urgency &middot; System age &middot; Photo
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* floating accent cards */}
          <div className="absolute -left-4 top-6 hidden rotate-[-4deg] rounded-2xl border border-white/10 bg-white/10 px-3 py-2 text-xs text-white/85 backdrop-blur-md lg:block">
            <span className="text-aqua">Lead captured</span> · Maria H.
          </div>
          <div className="absolute -right-3 bottom-24 hidden rotate-[3deg] rounded-2xl border border-sun/35 bg-sun/15 px-3 py-2 text-xs font-medium text-sun backdrop-blur-md lg:block">
            High-priority callback
          </div>
        </motion.div>
      </div>
    </section>
  );
}
